import { ExpandMore, Groups } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  List,
  ListItem,
  styled,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import ChatStatsIcon from '../assets/chat-stats-icon.svg';
import MessagesStatsIcon from '../assets/messages-stats-icon.svg';
import CarouselViewer from '../components/News/CarouselViewer';
import { AnalyticsEvent, useAnalytics } from '../components/Providers/AnalyticsProvider';
import { myFolder } from '../redux/consts';
import { selectAppStatus } from '../redux/reducers/appStatusReducer';
import { selectPrompts } from '../redux/reducers/promptReducer';
import { getHomePageStats } from '../services/stats';
import { StyledChatContainer } from '../theme/CustomComponents';
import { HomePageStats } from '../Types/stats';
import { useIsMobile } from '../util/useIsMobile';

const TitleBox = styled('div')(({ theme }) => {
  const isMobile = useIsMobile();
  return {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'start',
    marginBottom: '30px',
    width: isMobile ? '92vw' : '100%',
    alignItems: 'flex-start',
    color: theme.palette.utility.text.primary,
  };
});

const InfoCard = styled(Box)(({ theme }) => {
  const isMobile = useIsMobile();
  return {
    width: isMobile ? '92vw' : '280px',
    minWidth: isMobile ? '92vw' : '280px',
    minHeight: isMobile ? '100px' : '300px',
    textAlign: 'start',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: isMobile ? '20px' : '0',
    marginLeft: isMobile ? '-15px' : '0',
    maxHeight: '300px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    h4: {
      marginLeft: isMobile ? '0' : '10px',
      marginTop: '0px',
      marginBottom: '20px',
      textAlign: isMobile ? 'center' : 'start',
      color: theme.palette.utility.text.primary,
    },
    padding: '5px',
  };
});

const SmallInfoCard = styled(Box)(({ theme }) => {
  const isMobile = useIsMobile();
  return {
    width: isMobile ? '92vw' : '280px',
    minHeight: isMobile ? '100px' : '80px',
    textAlign: 'start',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: isMobile ? '20px' : '0',
    marginLeft: isMobile ? '-15px' : '0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    h4: {
      marginLeft: isMobile ? '0' : '20px',
      marginTop: '30px',
      marginBottom: '20px',
      textAlign: isMobile ? 'center' : 'start',
      color: theme.palette.utility.text.primary,
    },
  };
});

const StatsContainer = styled('div')(() => {
  const isMobile = useIsMobile();
  return {
    width: '100%',
    justifyContent: 'flex-start',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'center' : 'flex-start',
  };
});

const StyledStatsIcon = styled('img')(() => {
  const isMobile = useIsMobile();
  return {
    width: '60px',
    marginRight: isMobile ? '0' : '20px',
    marginBottom: isMobile ? '10px' : '0',
  };
});

interface CustomAccordionProps extends AccordionProps {
  backgroundColor?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CustomAccordion = styled(({ backgroundColor, ...props }: CustomAccordionProps) => (
  <Accordion {...props} />
))(({ backgroundColor }) => ({
  marginBottom: '10px',
  '& .MuiAccordionSummary-root': {
    backgroundColor: backgroundColor || 'defaultBackgroundColor',
  },
  '& .MuiAccordionDetails-root': {
    backgroundColor: backgroundColor || 'defaultBackgroundColor',
    paddingLeft: '10px',
  },
}));

const News = (): JSX.Element => {
  const analytics = useAnalytics();

  const messageContainerRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<HomePageStats>({
    numUsers: 0,
    numActiveConversations: 0,
    numMessages: 0,
  });
  const { appName } = useSelector(selectAppStatus);
  const { topPrompts } = useSelector(selectPrompts);
  const [shouldFetchStats, setShouldFetchStats] = useState<boolean>(true);

  useEffect(() => {
    const fetchStats = async () => {
      const newStats = await getHomePageStats();
      setStats(newStats);
      setShouldFetchStats(false);
    };
    if (shouldFetchStats) {
      fetchStats();
    }
  }, [shouldFetchStats]);

  const isMobile = useIsMobile();

  return (
    <div
      style={{ overflow: 'auto', width: '100%', height: 'calc(100% - 200px)' }}
      ref={messageContainerRef}
    >
      <StyledChatContainer maxWidth="md">
        <div
          style={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            // paddingTop: '20px',
          }}
        >
          <div>
            <div>
              <TitleBox>
                <Typography variant="h3" sx={{ marginBottom: '10px', fontWeight: 700 }}>
                  {`Welcome to ${appName}`}
                </Typography>
                <Typography variant="body1" sx={{ color: '#070154' }}>
                  {appName} is a conversational AI chatbot you can use to generate ideas, create
                  draft presentations, or just brainstorm. Happy chatting!
                </Typography>
              </TitleBox>
              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ width: '92vw' }}>
                    <CustomAccordion backgroundColor="#F900D3">
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6" sx={{ color: '#062541', fontWeight: '700' }}>
                          Stats
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <InfoCard>
                          <div
                            style={{
                              flexDirection: isMobile ? 'row' : 'column',
                              justifyContent: 'space-between',
                              margin: '0 20px 20px 20px',
                              gap: '30px',
                              display: 'flex',
                            }}
                          >
                            <StatsContainer>
                              <Groups
                                style={{
                                  color: 'white',
                                  width: '60px',
                                  height: '40px',
                                  marginBottom: '10px',
                                  paddingTop: '0',
                                }}
                              />
                              <Typography>
                                <span style={{ fontWeight: 'bold' }}>{stats.numUsers}</span> Users
                              </Typography>
                            </StatsContainer>
                            <StatsContainer>
                              <img
                                src={ChatStatsIcon}
                                style={{
                                  width: '50px',
                                  marginTop: '10px',
                                  marginBottom: '10px',
                                }}
                              />
                              <Typography>
                                <span style={{ fontWeight: 'bold' }}>
                                  {stats.numActiveConversations}
                                </span>{' '}
                                Conversations
                              </Typography>
                            </StatsContainer>
                            <StatsContainer>
                              <img
                                src={MessagesStatsIcon}
                                style={{
                                  width: '50px',
                                  marginTop: '10px',
                                  marginBottom: '10px',
                                }}
                              />
                              <Typography>
                                <span style={{ fontWeight: 'bold' }}>{stats.numMessages}</span>{' '}
                                Messages
                              </Typography>
                            </StatsContainer>
                          </div>
                        </InfoCard>
                      </AccordionDetails>
                    </CustomAccordion>
                    <CustomAccordion backgroundColor="#0047FF">
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6" sx={{ color: '#062541', fontWeight: '700' }}>
                          Prompts
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <InfoCard>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              gap: '10px',
                              margin: '0 20px 20px 20px',
                              flexGrow: 1,
                            }}
                          >
                            <Typography>
                              Think of them as little{' '}
                              <span style={{ color: '#FBB917' }}>“head starts”</span> that can help
                              you quickly get the info you want.
                            </Typography>

                            <br />

                            <Typography>Trending Prompts:</Typography>
                            <List sx={{ pt: '0px' }}>
                              {topPrompts.map((prompt, index) => (
                                <ListItem key={index} sx={{ pt: '0px', pl: '0px' }}>
                                  <Link
                                    to={`/folder/${myFolder.id}/chat?prompt=${prompt.id}`}
                                    style={{
                                      color: 'black',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    <Typography>
                                      🚀 {prompt.title} - {prompt.createdByName}
                                    </Typography>
                                  </Link>
                                </ListItem>
                              ))}
                            </List>
                          </div>
                        </InfoCard>
                      </AccordionDetails>
                    </CustomAccordion>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      gap: '10px',
                      marginBottom: '10px',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <InfoCard
                      sx={{
                        bgcolor: '#E8EEF8',
                        '&:hover': { cursor: 'default' },
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          flexDirection: 'column',
                          margin: '0 10px 10px 10px',
                          gap: '10px',
                          display: 'flex',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            height: '20px',
                            color: '#0047FF',
                            margin: 0,
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          PROMPTS
                        </Typography>
                        <Typography>
                          Think of them as little{' '}
                          <span style={{ color: '#F900D3' }}>“head starts”</span> that can help you
                          quickly get the info you want.
                        </Typography>
                        <br />
                        <Typography>Trending Prompts:</Typography>
                        <List sx={{ pt: '0px' }}>
                          {topPrompts.map((prompt, index) => (
                            <ListItem key={index} sx={{ pt: '0px', pl: '0px' }}>
                              <Link
                                to={`/folder/${myFolder.id}/chat?prompt=${prompt.id}`}
                                onClick={() => {
                                  analytics.event(
                                    AnalyticsEvent.WM_NIGEL_HOME_TRENDING_PROMPTS,
                                    {
                                      id: prompt.id,
                                    },
                                    { promptId: prompt.id, promptTrendingIndex: index }
                                  );
                                }}
                                style={{
                                  color: 'black',
                                  textDecoration: 'underline',
                                }}
                              >
                                <Typography>
                                  🚀 {prompt.title} - {prompt.createdByName}
                                </Typography>
                              </Link>
                            </ListItem>
                          ))}
                        </List>
                      </div>
                    </InfoCard>
                    <CarouselViewer />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      gap: '10px',
                      marginBottom: '20px',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <SmallInfoCard sx={{ bgcolor: '#070154', '&:hover': { cursor: 'default' } }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-end',
                          margin: '10px',
                          position: 'relative',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            minWidth: '200px',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#F900D3', fontWeight: '700', fontSize: '1rem' }}
                          >
                            Users
                          </Typography>
                          <Typography variant="h5" sx={{ color: 'white' }}>
                            <span style={{ fontWeight: 'bold' }}>{stats.numUsers}</span>
                          </Typography>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            alignItems: 'center',
                            position: 'relative',
                            flexDirection: 'column',
                          }}
                        >
                          <Groups style={{ color: 'white', width: '50px', height: '50px' }} />
                        </div>
                      </div>
                    </SmallInfoCard>
                    <SmallInfoCard sx={{ bgcolor: '#070154', '&:hover': { cursor: 'default' } }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          height: '100%',
                          alignItems: 'center',
                          margin: '10px',
                          position: 'relative',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            minWidth: '200px',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#F900D3', fontWeight: '700', fontSize: '1rem' }}
                          >
                            Chats Today
                          </Typography>
                          <Typography variant="h5" sx={{ color: 'white' }}>
                            <span style={{ fontWeight: 'bold' }}>
                              {stats.numActiveConversations}
                            </span>
                          </Typography>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            width: '100%',
                            alignItems: 'flex-end',
                            position: 'relative',
                            flexDirection: 'column',
                          }}
                        >
                          <StyledStatsIcon
                            src={ChatStatsIcon}
                            style={{ width: '60px', height: '60px', marginBottom: '-10px' }}
                          />
                        </div>
                      </div>
                    </SmallInfoCard>
                    <SmallInfoCard sx={{ bgcolor: '#070154' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          height: '100%',
                          alignItems: 'center',
                          margin: '10px',
                          position: 'relative',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            minWidth: '200px',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#F900D3', fontWeight: '700', fontSize: '1rem' }}
                          >
                            Messages
                          </Typography>
                          <Typography variant="h5" sx={{ color: 'white' }}>
                            <span style={{ fontWeight: 'bold' }}>{stats.numMessages}</span>
                          </Typography>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            width: '100%',
                            alignItems: 'flex-end',
                            position: 'relative',
                            flexDirection: 'column',
                          }}
                        >
                          <StyledStatsIcon
                            src={MessagesStatsIcon}
                            style={{
                              filter: 'brightness(0) invert(1)',
                              width: '60px',
                              height: '60px',
                            }}
                          />
                        </div>
                      </div>
                    </SmallInfoCard>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </StyledChatContainer>
    </div>
  );
};

export default News;
